import { DataFile, Dataset, Model, ReadAcross } from "../../api/models";
import { useUpdateDataFileDataFileUpdateDataFileHashPut, useUpdateDatasetDatasetUpdateDatasetHashPut, useUpdateModelModelUpdateModelHashPut, useUpdateReadacrossReadacrossUpdateReadacrossIdPut } from "../../api/generated/fastAPI";


const useUpdate = ({
    itemToUpdate,
    refetchFn,
    setPopupMessage,
}: {
    itemToUpdate: DataFile | Dataset | ReadAcross | null,
    refetchFn: () => void,
    setPopupMessage: (message: string, isSuccess: boolean) => void,
}) => {
    const updateDataFileMutation = useUpdateDataFileDataFileUpdateDataFileHashPut();
    const updateDatasetMutation = useUpdateDatasetDatasetUpdateDatasetHashPut();
    const updateRAMutation = useUpdateReadacrossReadacrossUpdateReadacrossIdPut();
    const updateModelMutation = useUpdateModelModelUpdateModelHashPut();

    const updateDataFile = async (name: string, value: any) => {
        const dataFile = itemToUpdate as DataFile | null;

        if (dataFile) {
            let updatedFile: DataFile = {...dataFile};

            const generalKeys = ['name', 'created_by', 'owner', 'creation_date'];
            const fileKeys = ['tags'];
            const datafileKeys = ['description', 'measurement_type', 'target', 'organism'];

            if (generalKeys.includes(name)) {
                updatedFile = {
                    ...dataFile,
                    [name]: value,
                }
            } else if (fileKeys.includes(name)) {
                updatedFile = {
                    ...dataFile,
                    file_metadata: {
                        ...dataFile.file_metadata!,
                        [name]: value,
                    }
                }
            } else if (datafileKeys.includes(name)) {
                updatedFile = {
                    ...dataFile,
                    datafile_metadata: {
                        ...dataFile.datafile_metadata!,
                        [name]: value,
                    }
                }
            }

            try {
                await updateDataFileMutation.mutateAsync({
                    dataFileHash: dataFile.file_hash!,
                    data: updatedFile,
                })
                
                refetchFn();
                setPopupMessage(`${dataFile.name ? dataFile.name : 'Datafile'} was successfully updated!`, true);
            } catch (err:any) {
                setPopupMessage('We encountered an issue updating this data file. Please try again.', false);
            }
        }
    };

    const updateDataset = async (name: string, value: any) => {
        const dataset = itemToUpdate as Dataset | null;

        if (dataset) {
            let updatedDataset: Dataset = {...dataset};

            const generalKeys = ['name', 'created_by', 'owner', 'creation_date'];
            const fileKeys = ['tags'];
            const datasetKeys = ['description'];

            if (generalKeys.includes(name)) {
                updatedDataset = {
                    ...dataset,
                    [name]: value,
                }
            } else if (fileKeys.includes(name)) {
                updatedDataset = {
                    ...dataset,
                    file_metadata: {
                        ...dataset.file_metadata!,
                        [name]: value,
                    }
                }
            } else if (datasetKeys.includes(name)) {
                updatedDataset = {
                    ...dataset,
                    dataset_metadata: {
                        ...dataset.dataset_metadata!,
                        [name]: value,
                    }
                }
            }

            try {
                await updateDatasetMutation.mutateAsync({
                    datasetHash: dataset.file_hash!,
                    data: updatedDataset,
                })
                refetchFn();
                setPopupMessage(`${dataset.name ? dataset.name : 'Dataset'} was successfully updated!`, true);
            } catch (err:any) {
                setPopupMessage('We encountered an issue updating this dataset. Please try again.', false);
            }
        }
    };

    const updateModel = async (name: string, value: any) => {
        const model = itemToUpdate as Model;

        if (model) {
            let updatedModel: Model = {...model};

            const generalKeys = ['name', 'created_by', 'owner', 'creation_date'];
            const fileKeys = ['tags'];

            if (generalKeys.includes(name)) {
                updatedModel = {
                    ...model,
                    [name]: value,
                }
            } else if (fileKeys.includes(name)) {
                updatedModel = {
                    ...model,
                    file_metadata: {
                        ...model.file_metadata,
                        [name]: value,
                    }
                }
            } 

            try {
                await updateModelMutation.mutateAsync({
                    modelHash: model.file_hash!,
                    data: updatedModel,
                })
    
                refetchFn();
                setPopupMessage('Model was successfully updated!', true);
            } catch (err:any) {
                setPopupMessage('We encountered an issue updating this model. Please try again.', false);
            }
        }
    }

    const updateRA = async (name: string, value: any) => {
        const readacross = itemToUpdate as ReadAcross | null;

        if (readacross) {
            let updatedRA: ReadAcross = {...readacross};

            const generalKeys = ['name', 'created_by', 'owner', 'creation_date'];
            const fileKeys = ['tags'];

            if (generalKeys.includes(name)) {
                updatedRA = {
                    ...readacross,
                    [name]: value,
                }
            } else if (fileKeys.includes(name)) {
                updatedRA = {
                    ...readacross,
                    readacross_metadata: {
                        ...readacross.readacross_metadata,
                        [name]: value,
                    }
                }
            } 

            try {
                await updateRAMutation.mutateAsync({
                    readacrossId: readacross.id!.toString(),
                    data: updatedRA,
                })
    
                refetchFn();
                setPopupMessage(`${readacross.name ? readacross.name : 'Read-across prediction'} was successfully updated!`, true);
            } catch (err:any) {
                setPopupMessage('We encountered an issue updating this read-across prediction. Please try again.', false);
            }
        }
    }

    return {
        updateDataFile,
        updateDataset,
        updateModel,
        updateRA,
    }
};

export default useUpdate;